import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import api from "../../util/api";

const initialState = {
  createSupplier: {
    loading: false,
  },
  importSupplier: {
    loading: false,
  },
  getSupplier: {
    loading: false,
    data: [],
    pagination: {
      page: 1,
      pageSize: 10,
      totalItems: 0,
    },
  },
  deleteSupplier: {
    loading: false,
  },
  editSupplier: {
    loading: false,
  },
  searchAndGetSupplier: {
    loading: false,
    data: [],
  },
  deleteAllSupplier: {
    loading: false,
  },
  deleteSelectedSupplier: {
    loading: false,
  },
};

//Supplier API call
const asyncCreateSupplier = createAsyncThunk(
  "admin/createSupplier",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await api.post("/api/suppliers/create", payload);
      return response;
    } catch (error) {
      return rejectWithValue({
        message: error.response?.data?.message,
      });
    }
  }
);

const asyncImportSupplier = createAsyncThunk(
  "admin/importSupplier",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await api.post("/api/suppliers/import", payload, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      return response;
    } catch (error) {
      return rejectWithValue({
        message: error.response?.data?.message,
      });
    }
  }
);

const asyncGetSupplier = createAsyncThunk(
  "admin/getSupplier",
  async (payload, { rejectWithValue }) => {
    const {
      page = 1,
      limit = 10,
      sortByColumn = "",
      sortOrder = "",
      q = "",
    } = payload;
    try {
      const response = await api.get(
        `/api/suppliers?page=${page}&limit=${limit}&sortByColumn=${sortByColumn}&sortOrder=${sortOrder}&q=${q}`
      );
      return response;
    } catch (error) {
      return rejectWithValue({
        message: error.response?.data?.message,
      });
    }
  }
);

const asyncDeleteSupplier = createAsyncThunk(
  "admin/deleteSupplier",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await api.delete(`/api/suppliers/${payload}`);
      return response;
    } catch (error) {
      return rejectWithValue({
        message: error.response?.data?.message,
      });
    }
  }
);

const asyncEditSupplier = createAsyncThunk(
  "admin/editSupplier",
  async (payload, { rejectWithValue }) => {
    const { id, ...restPayload } = payload;
    try {
      const response = await api.put(`/api/suppliers/${id}`, restPayload);
      return response;
    } catch (error) {
      return rejectWithValue({
        message: error.response?.data?.message,
      });
    }
  }
);

const asyncSearchAndGetSupplier = createAsyncThunk(
  "admin/searchAndGetSupplier",
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get(`/api/suppliers/search`);
      return response;
    } catch (error) {
      return rejectWithValue({
        message: error.response?.data?.message,
      });
    }
  }
);

const asyncDeleteAllSupplier = createAsyncThunk(
  "admin/deleteAllSupplier",
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.delete(`/api/suppliers/delete-all`);
      return response;
    } catch (error) {
      return rejectWithValue({
        message: error.response?.data?.message,
      });
    }
  }
);

const asyncDeleteSelectedSupplier = createAsyncThunk(
  "admin/deleteSelectedSupplier",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await api.post(`/api/suppliers/delete-many`, payload);
      return response;
    } catch (error) {
      return rejectWithValue({
        message: error.response?.data?.message,
      });
    }
  }
);

export const supplierSlice = createSlice({
  name: "supplier",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    // create supplier
    builder.addCase(asyncCreateSupplier.pending, (state) => {
      state.createSupplier = {
        loading: true,
      };
    });
    builder.addCase(asyncCreateSupplier.fulfilled, (state) => {
      state.createSupplier = {
        loading: false,
      };
    });
    builder.addCase(asyncCreateSupplier.rejected, (state) => {
      state.createSupplier = {
        loading: false,
      };
    });
    // import supplier
    builder.addCase(asyncImportSupplier.pending, (state) => {
      state.importSupplier = {
        loading: true,
      };
    });
    builder.addCase(asyncImportSupplier.fulfilled, (state) => {
      state.importSupplier = {
        loading: false,
      };
    });
    builder.addCase(asyncImportSupplier.rejected, (state) => {
      state.importSupplier = {
        loading: false,
      };
    });
    // get supplier
    builder.addCase(asyncGetSupplier.pending, (state) => {
      state.getSupplier = {
        loading: true,
        data: state.getSupplier.data || [],
        pagination: state.getSupplier.pagination,
      };
    });
    builder.addCase(asyncGetSupplier.fulfilled, (state, action) => {
      state.getSupplier = {
        loading: false,
        data: action.payload.data.results || [],
        pagination:
          action.payload.data.pagination || state.getSupplier.pagination,
      };
    });
    builder.addCase(asyncGetSupplier.rejected, (state) => {
      state.getSupplier = {
        loading: false,
        data: [],
        pagination: {
          page: 1,
          limit: 10,
          count: 0,
        },
      };
    });
    // delete supplier
    builder.addCase(asyncDeleteSupplier.pending, (state) => {
      state.deleteSupplier = {
        loading: true,
      };
    });
    builder.addCase(asyncDeleteSupplier.fulfilled, (state) => {
      state.deleteSupplier = {
        loading: false,
      };
    });
    builder.addCase(asyncDeleteSupplier.rejected, (state) => {
      state.deleteSupplier = {
        loading: false,
      };
    });
    // edit supplier
    builder.addCase(asyncEditSupplier.pending, (state) => {
      state.editSupplier = {
        loading: true,
      };
    });
    builder.addCase(asyncEditSupplier.fulfilled, (state) => {
      state.editSupplier = {
        loading: false,
      };
    });
    builder.addCase(asyncEditSupplier.rejected, (state) => {
      state.editSupplier = {
        loading: false,
      };
    });
    // search and get supplier
    builder.addCase(asyncSearchAndGetSupplier.pending, (state) => {
      state.searchAndGetSupplier = {
        loading: true,
        data: state.searchAndGetSupplier.data || [],
      };
    });
    builder.addCase(asyncSearchAndGetSupplier.fulfilled, (state, action) => {
      state.searchAndGetSupplier = {
        loading: false,
        data: action.payload.data.results || state.searchAndGetSupplier.data,
      };
    });
    builder.addCase(asyncSearchAndGetSupplier.rejected, (state) => {
      state.searchAndGetSupplier = {
        loading: false,
        data: [],
      };
    });
    // delete all supplier
    builder.addCase(asyncDeleteAllSupplier.pending, (state) => {
      state.deleteAllSupplier = {
        loading: true,
      };
    });
    builder.addCase(asyncDeleteAllSupplier.fulfilled, (state) => {
      state.deleteAllSupplier = {
        loading: false,
      };
    });
    builder.addCase(asyncDeleteAllSupplier.rejected, (state) => {
      state.deleteAllSupplier = {
        loading: false,
      };
    });
    // delete selected supplier
    builder.addCase(asyncDeleteSelectedSupplier.pending, (state) => {
      state.deleteSelectedSupplier = {
        loading: true,
      };
    });
    builder.addCase(asyncDeleteSelectedSupplier.fulfilled, (state) => {
      state.deleteSelectedSupplier = {
        loading: false,
      };
    });
    builder.addCase(asyncDeleteSelectedSupplier.rejected, (state) => {
      state.deleteSelectedSupplier = {
        loading: false,
      };
    });
  },
});

export {
  asyncCreateSupplier,
  asyncImportSupplier,
  asyncGetSupplier,
  asyncDeleteSupplier,
  asyncEditSupplier,
  asyncSearchAndGetSupplier,
  asyncDeleteAllSupplier,
  asyncDeleteSelectedSupplier,
};

export default supplierSlice.reducer;
